import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";
import { cn } from "@/lib/utils";

export interface TagOption {
  id: number;
  name: string;
  slug?: string;
}

interface TagPickerRowProps {
  label: string;
  options: TagOption[];
  selectedIds: number[];
  /** Omit (or pass undefined) to render in read-only mode. */
  onChange?: (ids: number[]) => void;
  isModified?: boolean;
  /** Show a required asterisk after the label. */
  required?: boolean;
  isLoading?: boolean;
  /** Placeholder when no tags are selected. Defaults to "No data yet". */
  emptyText?: string;
  /**
   * Tag IDs that are system-derived from the ingredient set. Rendered
   * in a muted tone so the clinician can tell them apart from chips they
   * added by hand. A locked-derived tag (also in `lockedIds`) shows a
   * helper icon; a removable-derived tag shows an X but is kept in the
   * "Removed" subrow so it can be restored if dropped by mistake.
   */
  derivedIds?: number[];
  /**
   * Tag IDs the clinician is NOT allowed to remove (e.g. allergens
   * inherited from a recipe ingredient). These render a helper (info)
   * icon instead of an X; clicking it fires `onLockedRemoveAttempt`.
   */
  lockedIds?: number[];
  /**
   * Per-tag origin label (e.g. "from Cow milk"). Rendered as a
   * `title` tooltip on the chip so the clinician can see where an
   * inherited tag came from.
   */
  originByTagId?: Record<number, string>;
  /**
   * Fired when the clinician taps the helper icon on a locked chip. The
   * parent surfaces the explanation (typically a destructive toast with
   * a "Change ingredient" action).
   */
  onLockedRemoveAttempt?: (tagId: number) => void;
}

/**
 * Compact one-row picker for a multi-select tag category.
 *
 * Mirrors the EHR Active Symptoms / Allergens widget pattern: label on the
 * left, selected chips inline, a `+` button on the right that opens a dialog
 * with a search box and the available options as click-to-add chips. Selected
 * chips render with an `X` to remove.
 *
 * Pass `onChange` to enable editing; omit it for a read-only view.
 */
export const TagPickerRow = ({
  label,
  options,
  selectedIds,
  onChange,
  isModified,
  required,
  isLoading,
  emptyText = "No data yet",
  derivedIds,
  lockedIds,
  originByTagId,
  onLockedRemoveAttempt,
}: TagPickerRowProps) => {
  const readOnly = !onChange;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const lockedSet = useMemo(() => new Set(lockedIds ?? []), [lockedIds]);
  const derivedSet = useMemo(() => new Set(derivedIds ?? []), [derivedIds]);

  /**
   * Removable system-derived tags the clinician has dropped this session.
   * Kept locally so they can be restored from the "Removed" subrow if they
   * were removed by mistake. Pruned whenever an id re-enters the selection.
   */
  const [removedDerived, setRemovedDerived] = useState<number[]>([]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    setRemovedDerived((prev) => prev.filter((id) => !selectedIds.includes(id)));
  }, [selectedIds]);

  const selectedOptions = useMemo(
    () =>
      selectedIds
        .map((id) => options.find((o) => o.id === id))
        .filter((o): o is TagOption => Boolean(o)),
    [selectedIds, options]
  );

  const availableOptions = useMemo(() => {
    const remaining = options.filter((o) => !selectedIds.includes(o.id));
    if (!search) return remaining;
    const q = search.toLowerCase();
    return remaining.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, selectedIds, search]);

  const addId = (id: number) => {
    if (!onChange) return;
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
  };

  const removeId = (id: number) => {
    if (!onChange) return;
    if (lockedSet.has(id)) {
      onLockedRemoveAttempt?.(id);
      return;
    }
    onChange(selectedIds.filter((existing) => existing !== id));
    // A removable system-derived tag isn't gone for good — park it in the
    // "Removed" subrow so a mistaken removal can be undone.
    if (derivedSet.has(id)) {
      setRemovedDerived((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  const restoreId = (id: number) => {
    if (!onChange) return;
    if (!selectedIds.includes(id)) onChange([...selectedIds, id]);
    setRemovedDerived((prev) => prev.filter((existing) => existing !== id));
  };

  const removedDerivedOptions = useMemo(
    () =>
      removedDerived
        .map((id) => options.find((o) => o.id === id))
        .filter((o): o is TagOption => Boolean(o)),
    [removedDerived, options]
  );

  return (
    <>
      <div className="flex items-start gap-3 py-2 border-b border-border/60 last:border-b-0">
        <span
          className={cn(
            "z-eyebrow text-muted-foreground shrink-0 w-[160px] pt-1.5",
            isModified && "text-warning font-semibold"
          )}
        >
          {label}
          {required && <span className="text-destructive"> *</span>}
          {selectedIds.length > 0 && (
            <span className="z-hint text-muted-foreground/70 ml-1">
              ({selectedIds.length})
            </span>
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => {
                const isLocked = lockedSet.has(option.id);
                const isDerived = derivedSet.has(option.id) || isLocked;
                const origin = originByTagId?.[option.id];
                return (
                  <span
                    key={option.id}
                    title={
                      origin
                        ? `Inherited ${origin}${
                            isLocked
                              ? " — tap the helper icon for why it's locked"
                              : ""
                          }`
                        : undefined
                    }
                  >
                    <DismissibleChip
                      label={option.name}
                      onInfo={
                        isLocked
                          ? () => onLockedRemoveAttempt?.(option.id)
                          : undefined
                      }
                      onRemove={
                        readOnly || isLocked
                          ? undefined
                          : () => removeId(option.id)
                      }
                      className={cn(
                        isDerived &&
                          "border-border/50 bg-muted/50 text-muted-foreground"
                      )}
                    />
                  </span>
                );
              })
            ) : (
              <span className="z-caption text-muted-foreground/60 py-1">
                {emptyText}
              </span>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => setOpen(true)}
                disabled={isLoading}
                className="flex items-center justify-center size-6 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                title={`Add ${label.toLowerCase()}`}
              >
                <Plus className="size-3.5" />
              </button>
            )}
          </div>

          {/* Removed system-derived tags — kept restorable in case the
              clinician dropped one by mistake. */}
          {!readOnly && removedDerivedOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
              <span className="z-hint text-muted-foreground/70 pr-0.5">
                Removed:
              </span>
              {removedDerivedOptions.map((option) => (
                <span key={`removed-${option.id}`} title="Restore this tag">
                  <DismissibleChip
                    label={option.name}
                    onAdd={() => restoreId(option.id)}
                    className="border-dashed border-border/60 bg-transparent text-muted-foreground/70 line-through"
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {!readOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
            <DialogHeader className="p-6 pb-4 shrink-0">
              <DialogTitle className="z-h2 text-foreground">
                {label}
              </DialogTitle>
            </DialogHeader>

            <div className="h-px bg-primary/30 shrink-0" />

            <div className="flex flex-col">
              {selectedOptions.length > 0 && (
                <div className="px-6 pt-4 pb-3 shrink-0 space-y-1.5">
                  <p className="z-eyebrow text-muted-foreground">Selected</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedOptions.map((option) => (
                      <DismissibleChip
                        key={`selected-${option.id}`}
                        label={option.name}
                        onRemove={() => removeId(option.id)}
                        className="border-primary/40 bg-primary/10 text-primary"
                        removeClassName="bg-primary/15 text-primary/60 hover:bg-destructive/20 hover:text-destructive"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="px-6 pt-3 pb-6 space-y-3">
                <p className="z-secondary text-muted-foreground">
                  Tap a chip to add
                </p>
                <Command
                  shouldFilter={false}
                  className="overflow-visible bg-transparent"
                >
                  <CommandInput
                    placeholder="Search…"
                    value={search}
                    onValueChange={setSearch}
                    autoFocus
                    className="z-body"
                  />
                  <CommandList className="hidden">
                    <CommandEmpty />
                    <CommandGroup />
                  </CommandList>
                </Command>

                <div className="max-h-[260px] overflow-y-auto scrollbar-thin py-1">
                  {availableOptions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {availableOptions.map((option) => (
                        <DismissibleChip
                          key={option.id}
                          label={option.name}
                          onAdd={() => addId(option.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="z-caption text-muted-foreground/60 py-2">
                      {options.length === 0
                        ? "No options available."
                        : selectedIds.length === options.length
                          ? "All options selected."
                          : "No matches."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TagPickerRow;
